
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import ColorPicker from '@components/settings/ColorPicker.vue';
import TagIconPicker from '@components/settings/tag/TagIconPicker.vue';
import SettingsTitleBar from '@components/settings/SettingsTitleBar.vue';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import SettingsDelete from '@components/settings/SettingsDelete.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppEditableHeader from '@components/new-styleguide/AppEditableHeader.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import { LoadState } from '@/utils/types';
import SettingsPanel from '@components/settings/SettingsPanel.vue';
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue';
import { TagMutation } from '@/generated-clients/myTrips';
import { ruleV2Icons } from '@util/settingIcons'
import { TagWithRules } from '@/models/SettingsModels';
import deepCopy from '@/utils/copy';
import { channelSubtitle } from '@util/channelSubtitle';
import goBackWithFallback from '@util/goBackWithFallback';
import { RouterFrom } from '@/router/Routes';

const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: 'TagEdit',
  components: {
    AppPage,
    AppContent,
    AppEditableHeader,
    SettingsTitleBar,
    ColorPicker,
    TagIconPicker,
    SettingsFooter,
    SettingsDelete,
    SettingsPanel,
    AppListItem,
    InfoBoxWarning
  }
})
export default class TagEdit extends Vue {
  @SettingsModule.Getter('getTags')
  private tags: TagWithRules[];

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Action('fetchRulesV2')
  private fetchRules;

  @SettingsModule.Action('updateTag')
  private updateTag;

  @SettingsModule.Action('deleteTag')
  private deleteTag;

  @SettingsModule.Getter('getTagsStatus')
  private tagsStatus: LoadState;

  @RouterModule.State('from')
  private from: RouterFrom;

  private input: TagMutation = {
    name: '',
    iconName: null,
    color: ''
  };

  private disabled = true;
  private errorMessage = '';

  private deleteText = 'Sind Sie sicher, dass Sie die Kategorie löschen möchten?';

  async created() {
    const fetchNonTagsPromise = Promise.all[this.fetchRules(), this.fetchPilots()];

    await this.fetchTags();

    try {
      await fetchNonTagsPromise;
    } finally {
      this.input = deepCopy(this.tag);
      this.disabled = false;
    }
  }

  get rulesHeadline() {
    return 'Diese Kategorie ist Teil folgender Regel' + (this.associatedRuleItems.length > 1 ? 'n' : '');
  }

  get titleValue() {
    return this.isLoading ? '' : this.input.name;
  }

  get placeholder() {
    return this.isLoading ? 'Wird geladen...' : 'Name hier eingeben';
  }

  get backButton() {
    return { text: 'Zurück zu Kategorien', location: this.backLocation };
  }

  get backLocation() {
    return { name: 'tagsOverview' };
  }

  get tag() {
    return this.tags.find(p => p.id === this.id);
  }

  get associatedRuleItems(): ListItem[] {
    return (this.tag)
        ? this.tag.associatedRules
            .map(r => ({ id: r.id, title: r.name, subtitle: channelSubtitle(r), ...ruleV2Icons(r), location: { name: 'ruleEdit', params: { id: r.id.toString() } }, hoverText: 'Regel bearbeiten' }))
        : [];
  }

  get title() {
    return this.tag ? this.tag.name : '';
  }

  get deleteModalHeader() {
    return `Kategorie ${this.title} löschen`;
  }

  get id() {
    return parseInt(this.$route.params.id);
  }

  get nameError() {
    return this.input && this.input.name.length === 0;
  }

  get errorsExist() {
    return this.nameError;
  }

  get errorLoading() {
    return this.tagsStatus === LoadState.Error;
  }

  get showAssociatedRules() {
    return !this.isLoading && this.associatedRuleItems.length > 0;
  }

  get isLoading() {
    return this.tagsStatus === LoadState.Loading;
  }

  private async submit() {
    if (!this.errorsExist) {
      this.disabled = true;

      try {
        await this.updateTag({ id: this.tag.id, tag: this.input });
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } catch (error) {
        this.errorMessage = 'Fehler beim Speichern der Kategorie. Bitte versuchen sie es später erneut.'
      } finally {
        this.disabled = false;
      }
    }
  }

  private deleteTagButton() {
    if (window.confirm(this.deleteText)) {
      this.handleTagDelete();
    }
  }

  private async handleTagDelete() {
    this.disabled = true;
    try {
      await this.deleteTag({ id: this.tag.id });
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    } catch (error) {
      this.errorMessage = 'Fehler beim Löschen der Kategorie. Bitte versuchen sie es später erneut.'
    } finally {
      this.disabled = false;
    }
  }
}
